import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Input from "../ui/Input";
import Button from "../ui/Button";
import ModelSelect from "../ui/ModelSelect";

const PlastiqueAndFil = () => {
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [plastiqueReal, setPlastiqueReal] = useState("");
  const [filReal, setFilReal] = useState("");
  const [otherReal, setOtherReal] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [totals, setTotals] = useState({
    plastiqueReal: 0,
    filReal: 0,
    otherReal: 0,
  });

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await api.get("/models");
        setModels(response.data);
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    };

    fetchModels();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (selectedModel) {
        try {
          const response = await api.get(`/plastique_fil/${selectedModel}`);
          setSubmissions(response.data.submissions || []);
          setTotals(response.data.totals || {
            plastiqueReal: 0,
            filReal: 0,
            otherReal: 0,
          });
        } catch (error) {
          setSubmissions([]);
          setTotals({
            plastiqueReal: 0,
            filReal: 0,
            otherReal: 0,
          });
          console.error("Error fetching Plastique and Fil data:", error);
        }
      }
    };

    fetchData();
  }, [selectedModel]);

  const handleModelChange = (e) => {
    setSelectedModel(e.target.value);
    setPlastiqueReal("");
    setFilReal("");
    setOtherReal("");
  };

  const handleChange = (name, value) => {
    if (name === "plastiqueReal") {
      setPlastiqueReal(value);
    } else if (name === "filReal") {
      setFilReal(value);
    } else if (name === "otherReal") {
      setOtherReal(value);
    }
  };

  const handleSave = async () => {
    // Check if at least one field has a value
    if (!plastiqueReal && !filReal && !otherReal) {
      toast.error("At least one field must have a value");
      return;
    }

    setIsButtonDisabled(true);
    try {
      const response = await api.post(`/plastique_fil/${selectedModel}`, {
        plastiqueReal: plastiqueReal ? parseInt(plastiqueReal, 10) : null,
        filReal: filReal ? parseInt(filReal, 10) : null,
        otherReal: otherReal ? parseInt(otherReal, 10) : null,
      });

      // Add new submission to the list
      setSubmissions([response.data.submission, ...submissions]);
      setTotals(response.data.totals);

      // Clear form
      setPlastiqueReal("");
      setFilReal("");
      setOtherReal("");

      toast.success("Data saved successfully");
    } catch (error) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("Error saving data");
      }
      console.error("Error saving Plastique and Fil data:", error);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const selectedModelData = models.find((model) => model.id == selectedModel);

  return (
    <div className="ml-[19%] pt-[6rem]">
      <ToastContainer />
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Plastique and Fil Real</h2>
        <div className="ml-7 mb-4 pr-6">
          <ModelSelect className="mt-4" value={selectedModel} onChange={handleModelChange} models={models} />
        </div>
      </div>
      
      {selectedModelData && (
        <div className="w-full pr-6">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-xl">
              {selectedModelData.category}
            </h3>
            <h3 className="font-semibold text-xl">
              {selectedModelData.client}
            </h3>
          </div>
        </div>
      )}

      {selectedModel && (
        <div className="flex flex-col items-center mx-auto mt-[2rem] mb-4 shadow-md w-fit p-10 rounded border">
          <div className="mt-5">
            <Input
              handleChange={(name, value) =>
                handleChange("plastiqueReal", value)
              }
              name={"plastiqueReal"}
              label="Plastique Real (Optional)"
              text={plastiqueReal}
            />
          </div>
          <div className="mt-7">
            <Input
              handleChange={(name, value) => handleChange("filReal", value)}
              name={"filReal"}
              label="Fil Real (Optional)"
              text={filReal}
            />
          </div>
          <div className="mt-7">
            <Input
              handleChange={(name, value) => handleChange("otherReal", value)}
              name={"otherReal"}
              label="Other Real (Optional)"
              text={otherReal}
            />
          </div>
          <Button
            classes={"bg-blue-500 mt-10"}
            handlePress={handleSave}
            disabled={isButtonDisabled}
            container={"w-full"}
          >
            Save
          </Button>
        </div>
      )}

      {/* Totals Section */}
      {selectedModel && totals && (
        <div className="mt-8 pr-6">
          <h3 className="text-lg font-semibold mb-4">Totals</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-100 p-4 rounded-lg text-center">
              <div className="text-sm text-gray-600">Total Plastique</div>
              <div className="text-xl font-bold text-blue-800">{totals.plastiqueReal}</div>
            </div>
            <div className="bg-green-100 p-4 rounded-lg text-center">
              <div className="text-sm text-gray-600">Total Fil</div>
              <div className="text-xl font-bold text-green-800">{totals.filReal}</div>
            </div>
            <div className="bg-purple-100 p-4 rounded-lg text-center">
              <div className="text-sm text-gray-600">Total Other</div>
              <div className="text-xl font-bold text-purple-800">{totals.otherReal}</div>
            </div>
          </div>
        </div>
      )}

      {/* Submissions History */}
      {selectedModel && submissions.length > 0 && (
        <div className="mt-8 pr-6">
          <h3 className="text-lg font-semibold mb-4">Submission History</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    Plastique
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    Fil
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    Other
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {submissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-b">
                      {submission.created_at}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-b">
                      {submission.plastiqueReal || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-b">
                      {submission.filReal || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-b">
                      {submission.otherReal || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlastiqueAndFil;
